"use strict";
var $dyn;
(function ($dyn) {
    var serverForm;
    (function (serverForm) {
        /**
         * Definition of the function names used with calling between proxy and real InteractionManager.
         * @internal
         */
        var InteractionManagerFunction;
        (function (InteractionManagerFunction) {
            InteractionManagerFunction["queueCallbackCommandInteraction"] = "queueCallbackCommandInteraction";
            InteractionManagerFunction["queueThrottledCommandInteraction"] = "queueThrottledCommandInteraction";
            InteractionManagerFunction["queueInteraction"] = "queueInteraction";
            InteractionManagerFunction["queueTelemetryEvent"] = "queueTelemetryEvent";
            InteractionManagerFunction["flushTelemetryEvents"] = "flushTelemetryEvents";
            InteractionManagerFunction["removePendingPersonalization"] = "removePendingPersonalization";
        })(InteractionManagerFunction = serverForm.InteractionManagerFunction || (serverForm.InteractionManagerFunction = {}));
        serverForm.defaultChannelPoolSize = 3;
        /**
         * Queues interactions into a channel.
         * Designed to be the only entry point for queueing interactions to the server.
         * Only the Host window has a real InteractionManager, all popped out windows have Proxies which point to the host Manager.
         */
        var InteractionManager = /** @class */ (function () {
            function InteractionManager() {
                /** @internal Use channelPool() instead if possible */
                this._channelPool = [];
                /**
                 * Used when unblocking the UI in disconnect scenarios to prevent flushing events
                 * @internal
                 */
                this._isClientDisconnected = false;
                this._channelPoolSize = $dyn.serverForm.defaultChannelPoolSize;
                this._formToChannelMap = {};
                this._nextAvailableChannel = 0;
                this._windowIdToHandler = {};
                this._rootIdToWindowId = {};
                this.commandCallbacks = {};
                this._commandCallbackIdAllocator = 1;
                this._globalBlockingCount = 0;
                this._inboundBlocked = false;
                /**
                 * Used when the channel blocks due to retry attempts
                 */
                this._isChannelBlocking = false;
                this._processInteractionsTask = new $dyn.PerformanceTask("processInteractions", true);
                /**
                 * Handler that listens for postMessage events and reacts accordingly if it is for the desired class
                 * @remarks All PostMessage event handlers should ensure the following:
                 * 1. The `event.origin` is from the same location
                 * 2. The `event.data` is of the type we are expecting
                 * 3. The data contains some sort of unique identifier which our handler knows to look for and will not process if not found.
                 * @param event The message to handle
                 */
                this.handleMessages = function (event) {
                    if (event.origin !== $dyn.util.getOrigin($dyn.util.location.location)) {
                        // Do not allow communication if it is cross origin.
                        return;
                    }
                    var data = event.data;
                    // Sanity check. Ensure these are coming for the InteractionManager
                    if (typeof data === "object" && data.source === "InteractionManager") {
                        // In the case of calling Resend command interaction from a popout window: We need to remap the rootId to the popout windowId.
                        if (data.interaction && data.interaction.CommandName && data.interaction.CommandName === "Reserialize") {
                            $dyn.serverForm.interactionManager.addRootId(data.interaction.TargetId, data.interaction.windowId);
                        }
                        $dyn.serverForm.interactionManager.reconstructCallbackFunctions(data);
                        switch (data.commandName) {
                            case $dyn.serverForm.InteractionManagerFunction.queueCallbackCommandInteraction:
                                $dyn.serverForm.interactionManager.queueCallbackCommandInteraction(data.interaction, data.callback, data.channelOptions, data.shouldProcess, data.failureCallback);
                                break;
                            case $dyn.serverForm.InteractionManagerFunction.queueThrottledCommandInteraction:
                                $dyn.serverForm.interactionManager.queueThrottledCommandInteraction(data.interaction, data.callback, data.channelOptions, data.shouldProcess, data.throttleCallback, data.failureCallback);
                                break;
                            case $dyn.serverForm.InteractionManagerFunction.queueInteraction:
                                $dyn.serverForm.interactionManager.queueInteraction(data.interaction, data.channelOptions, data.shouldProcess, data.failureCallback);
                                break;
                            case $dyn.serverForm.InteractionManagerFunction.queueTelemetryEvent:
                                $dyn.serverForm.interactionManager.queueTelemetryEvent(data.event);
                                break;
                            case $dyn.serverForm.InteractionManagerFunction.flushTelemetryEvents:
                                $dyn.serverForm.interactionManager.flushTelemetryEvents();
                                break;
                            case $dyn.serverForm.InteractionManagerFunction.removePendingPersonalization:
                                $dyn.serverForm.interactionManager.removePendingPersonalization(data.targetId, data.property, data.channelOptions);
                            default:
                                break;
                        }
                    }
                };
            }
            InteractionManager.prototype.init = function () {
                // Add the default channel and other interaction channels to the channel pool
                for (var i = 1, len = this._channelPoolSize; i < len; i++) {
                    var channel = new $dyn.serverForm.ReliableChannel(i);
                    var interactionChannel = new $dyn.serverForm.InteractionChannel(channel);
                    interactionChannel.registerInteractionReceivedCallback(this._onInteractionReceived, this);
                    this._channelPool.push(interactionChannel);
                }
                $dyn.serverForm.defaultChannel().registerInteractionReceivedCallback(this._onInteractionReceived, this);
                this._channelPool.push($dyn.serverForm.defaultChannel());
                this._nextUnusedChannelId = 10;
            };
            InteractionManager.prototype.reset = function () {
                this._channelPoolSize = $dyn.serverForm.defaultChannelPoolSize;
                this._channelPool = [];
                this._formToChannelMap = {};
                this._nextAvailableChannel = 0;
                this.init();
            };
            /**
             * Queues a callback command interaction to the specified channel type
             * @param interaction The interaction to queue
             * @param callback Callback function for the interaction
             * @param channelOptions Which channel type to queue the interaction on
             * @param shouldProcess Determines whether process() should be called on the channel after queueing
             * @param failureCallback Callback function for if this interaction fails
             */
            InteractionManager.prototype.queueCallbackCommandInteraction = function (interaction, callback, channelOptions, shouldProcess, failureCallback) {
                var interactionChannel = this._getInteractionChannelByType(channelOptions);
                if (!interactionChannel) {
                    /// #DEBUG
                    $dyn.logError("An initialized interaction channel is required");
                    /// #ENDDEBUG
                    return;
                }
                this.registerCallback(interaction, callback);
                if (failureCallback) {
                    this.registerFailureCallback(interaction, failureCallback);
                }
                interactionChannel.queueCallbackCommandInteraction(interaction);
                if (shouldProcess) {
                    interactionChannel.process();
                }
            };
            /**
             * Queues a callback command interaction to the specified channel type
             * @param interaction Interaction to queue
             * @param callback Callback function for the interaction
             * @param channelOptions What type of channel to queue the interaction on
             * @param shouldProcess Determines whether process() should be called on the channel after queueing
             * @param throttleCallback Callback function to call if interaction gets taken out of the queue by throttling
             * @param failureCallback Callback function to call if the command was unable to execute
             */
            InteractionManager.prototype.queueThrottledCommandInteraction = function (interaction, callback, channelOptions, shouldProcess, throttleCallback, failureCallback) {
                var interactionChannel = this._getInteractionChannelByType(channelOptions);
                if (!interactionChannel) {
                    /// #DEBUG
                    $dyn.logError("An initialized interaction channel is required");
                    /// #ENDDEBUG
                    return;
                }
                if (throttleCallback) {
                    interaction.ThrottleCallback = throttleCallback;
                }
                if (failureCallback) {
                    this.registerFailureCallback(interaction, failureCallback);
                }
                interactionChannel.queueThrottledCommandInteraction(interaction, callback);
                if (shouldProcess) {
                    interactionChannel.process();
                }
            };
            /**
             * Queues an interaction to the specified channel type
             * @param interaction The interaction to queue
             * @param channelOptions Which type of channel to queue the interaction on
             * @param shouldProcess Determines if interactions should be processed on the channel after queueing
             * @param failureCallback Callback function to call if the command was unable to execute
             */
            InteractionManager.prototype.queueInteraction = function (interaction, channelOptions, shouldProcess, failureCallback) {
                var interactionChannel = this._getInteractionChannelByType(channelOptions);
                if (!interactionChannel) {
                    /// #DEBUG
                    $dyn.logError("An initialized interaction channel is required");
                    /// #ENDDEBUG
                    return;
                }
                if (failureCallback) {
                    this.registerFailureCallback(interaction, failureCallback);
                }
                interactionChannel.queueInteraction(interaction);
                if (shouldProcess) {
                    interactionChannel.process();
                }
            };
            InteractionManager.prototype.queueTelemetryEvent = function (event) {
                $dyn.telemetry._eventQueue.push(event);
            };
            InteractionManager.prototype.flushTelemetryEvents = function () {
                if (!this._isClientDisconnected && $dyn.length($dyn.telemetry._eventQueue) > 0) {
                    var interactions = [];
                    // Make a copy of the queue to send with the interaction
                    var interaction = {
                        $type: $dyn.serverForm.InteractionType.eventData,
                        EventData: $dyn.telemetry._eventQueue.slice(),
                    };
                    interactions.unshift(interaction);
                    $dyn.telemetry.clearEvents();
                    this.sendTelemetryEvents(interactions);
                }
            };
            InteractionManager.prototype.removePendingPersonalization = function (targetId, property, channelOptions) {
                var interactionChannel = this._getInteractionChannelByType(channelOptions);
                if (!interactionChannel) {
                    /// #DEBUG
                    $dyn.logError("An initialized interaction channel is required");
                    /// #ENDDEBUG
                    return;
                }
                interactionChannel.removePendingPersonalization(targetId, property);
            };
            InteractionManager.prototype.flushAllChannels = function () {
                for (var i = this._channelPool.length - 1; i >= 0; i--) {
                    var channel = this._channelPool[i];
                    channel.process();
                }
            };
            /**
             * Returns a clone of the channel pool list. Modifications to the returned array do not affect the original.
             */
            InteractionManager.prototype.channelPool = function () {
                return this._channelPool.slice(0);
            };
            InteractionManager.prototype.updateMapOnFormDelete = function (rootId) {
                if (rootId) {
                    if (this._formToChannelMap[rootId]) {
                        delete this._formToChannelMap[rootId];
                    }
                    if (this._rootIdToWindowId[rootId] !== undefined) {
                        delete this._rootIdToWindowId[rootId];
                    }
                }
            };
            InteractionManager.prototype.addWindowHandler = function (windowId, handler) {
                if (this._windowIdToHandler[windowId]) {
                    // Log Error?, we should not add a windowId more than once.
                }
                this._windowIdToHandler[windowId] = handler;
            };
            InteractionManager.prototype.addRootId = function (rootId, windowId) {
                this._rootIdToWindowId[rootId] = windowId;
            };
            /** @internal test only */
            InteractionManager.prototype.getClientWindowForFormId = function (rootId) {
                var windowId = this.getWindowIdFromRootId(rootId);
                return this._windowIdToHandler[windowId].window;
            };
            InteractionManager.prototype.getWindowIdFromRootId = function (rootId, defaultWindowId) {
                var windowId = this._rootIdToWindowId[rootId];
                if (windowId === null || windowId === undefined) {
                    windowId = defaultWindowId !== null && defaultWindowId !== undefined ? defaultWindowId : $dyn.serverForm._windowId;
                }
                return windowId;
            };
            InteractionManager.prototype._prepFormForPopout = function (rootId, windowId) {
                // Call on the interactionHandler of this rootId to prep this form for removal from the DOM.
                var interactionHandler = this._windowIdToHandler[windowId];
                if (interactionHandler) {
                    return interactionHandler._prepFormForPopout(rootId);
                }
                return false;
            };
            InteractionManager.prototype.detachFormForPopout = function (rootId, windowId) {
                // Call on the interactionHandler of this rootId to remove this form from that window's DOM.
                var interactionHandler = this._windowIdToHandler[windowId];
                if (interactionHandler) {
                    return interactionHandler.detachFormForPopout(rootId);
                }
                return false;
            };
            InteractionManager.prototype.registerCallback = function (interaction, callback) {
                // Register a callback for the interaction
                if (callback) {
                    if (!interaction.CallbackId) {
                        // A callback is provided, so we need to register it
                        var callbackId = (this._commandCallbackIdAllocator++).toString();
                        interaction.CallbackId = callbackId;
                    }
                    // Add the callback to the registration pool
                    this.commandCallbacks[interaction.CallbackId] = callback;
                }
            };
            InteractionManager.prototype.registerFailureCallback = function (interaction, callback) {
                // Register a callback for the failure of the interaction
                if (callback) {
                    if (!interaction.FailureCallbackId) {
                        // A callback is provided, so we need to register it
                        var callbackId = (this._commandCallbackIdAllocator++).toString();
                        interaction.FailureCallbackId = callbackId;
                    }
                    // Add the callback to the registration pool
                    this.commandCallbacks[interaction.FailureCallbackId] = callback;
                }
            };
            InteractionManager.prototype.unregisterCallback = function (callbackId) {
                // Unregister a callback for the interaction
                if (this.commandCallbacks[callbackId]) {
                    delete this.commandCallbacks[callbackId];
                }
            };
            /**
             * Invokes the callback with the provided callbackId.
             * If the callback is not defined, logs a warning and returns false.
             * Otherwise, tries to invoke the callback with the provided `param` value and returns true.
             * @param callbackId ID of the callback to invoke
             * @param param Parameters to send to the callback function
             */
            InteractionManager.prototype.invokeCallback = function (callbackId, param) {
                var callback = this.commandCallbacks[callbackId];
                if (!callback) {
                    $dyn.logError($dyn.format("Callback with id: %d was not defined in the list of commandCallbacks", callbackId));
                    return false;
                }
                if (typeof callback === "function") {
                    callback(param);
                }
                else if (typeof callback === "object") {
                    // Until 3674799 is done, we need to check if any objects are in the commandCallbacks array which represent callbacks on child windows.
                    if (callback._functionId && callback.windowId) {
                        var functionWindow = this._windowIdToHandler[callback.windowId];
                        var callbackWrapper = { callback: callback };
                        this._createCallbackFunction(functionWindow, callbackWrapper, "callback");
                        callbackWrapper.callback(param);
                    }
                }
                return true;
            };
            InteractionManager.prototype.cleanupPopout = function (windowId, closeForms) {
                // Clean up _windowIdToHandler
                delete this._windowIdToHandler[windowId];
                // Clean up _rootIdToWindowId;
                var keysForDeletion = [];
                // tslint:disable-next-line:forin
                for (var key in this._rootIdToWindowId) {
                    var value_1 = this._rootIdToWindowId[key];
                    // Potential number != string comparison
                    // tslint:disable-next-line:triple-equals
                    if (value_1 == windowId) {
                        keysForDeletion.push(key);
                    }
                }
                for (var i = 0, len = keysForDeletion.length; i < len; i++) {
                    var rootId = keysForDeletion[i];
                    // Only close forms if we are in a situation where we are told to.
                    if (closeForms) {
                        // Send RequestCloseCancel interaction.
                        var interaction = { $type: $dyn.serverForm.InteractionType.command, CommandName: "RequestCloseCancel", RootId: rootId, TargetId: rootId };
                        var channelOptions = {
                            channelType: $dyn.serverForm.InteractionChannelType.byRootId,
                            rootId: rootId,
                        };
                        $dyn.serverForm.interactionManager.queueInteraction(interaction, channelOptions, true);
                    }
                    delete this._rootIdToWindowId[rootId];
                }
            };
            InteractionManager.prototype.hasQueuedInteractions = function () {
                var count = 0;
                for (var i = 0, len = this._channelPool.length; i < len; i++) {
                    var ch = this._channelPool[i];
                    if (ch.interactionQueue) {
                        count += ch.interactionQueue.length;
                    }
                }
                return count > 0;
            };
            InteractionManager.prototype.hasPendingResponse = function (onlyDataAffectingInteractions) {
                if (onlyDataAffectingInteractions === void 0) { onlyDataAffectingInteractions = false; }
                for (var i = 0, len = this._channelPool.length; i < len; i++) {
                    var ch = this._channelPool[i];
                    if (ch.hasPendingResponse(onlyDataAffectingInteractions)) {
                        return true;
                    }
                }
                return false;
            };
            InteractionManager.prototype.processingInteractions = function () {
                for (var i = 0, len = this._channelPool.length; i < len; i++) {
                    var ch = this._channelPool[i];
                    if (ch.processingInteractions) {
                        return true;
                    }
                }
                return false;
            };
            InteractionManager.prototype.getRootFormId = function () {
                var activeForm = $dyn.shell.getCurrentRootForm();
                if (activeForm && activeForm.dynOptions && activeForm.dynOptions._serverId) {
                    return $dyn.context(activeForm).Id;
                }
                return null;
            };
            /**
             * Increments the global blocking flag
             * This flag is used to know when to notify a global broadcast to block user interaction
             */
            InteractionManager.prototype.incrementGlobalBlockingFlag = function (blockImmediate, description) {
                this._globalBlockingCount++;
                if (this._globalBlockingCount === 1) {
                    // Global blocking count has reached 1, notify all windows to increment their blocking count.
                    $dyn.serverForm._windowManager.notifyBlock(blockImmediate, description);
                }
            };
            /**
             * Decrements the global blocking flag
             * This flag is used to know when to notify a global broadcast to block user interaction
             */
            InteractionManager.prototype.decrementGlobalBlockingFlag = function () {
                if (this._globalBlockingCount > 0) {
                    this._globalBlockingCount--;
                    // Only call notify unblock if the value is changing from 1 to 0.
                    if (this._globalBlockingCount === 0) {
                        $dyn.serverForm._windowManager.notifyUnBlock();
                    }
                }
            };
            /**
             * Sets the global blocking flag to 0.
             * If the globalBlockingCount is not already 0 when this is called, sends a broadcast to unblock user interation.
             */
            InteractionManager.prototype.resetGlobalBlockingFlag = function () {
                var shouldNotify = this._globalBlockingCount > 0;
                this._globalBlockingCount = 0;
                if (shouldNotify) {
                    $dyn.serverForm._windowManager.notifyUnBlock();
                }
            };
            InteractionManager.prototype._getInteractionChannelByType = function (channelOptions) {
                var interactionChannel;
                switch (channelOptions.channelType) {
                    case $dyn.serverForm.InteractionChannelType.byRootId:
                        interactionChannel = this._getInteractionChannelByRootId(channelOptions.rootId);
                        break;
                    case $dyn.serverForm.InteractionChannelType.byElement:
                        interactionChannel = this._getInteractionChannelByElement(channelOptions.element);
                        break;
                    case $dyn.serverForm.InteractionChannelType.available:
                        interactionChannel = this._getAvailableInteractionChannel();
                        break;
                    case $dyn.serverForm.InteractionChannelType.any:
                        interactionChannel = this._getInteractionChannel(!channelOptions.formPart);
                        break;
                    case $dyn.serverForm.InteractionChannelType.byChannel:
                        interactionChannel = channelOptions.channel;
                        break;
                    default:
                        if ($dyn.serverForm.defaultChannel) {
                            interactionChannel = $dyn.serverForm.defaultChannel();
                        }
                        break;
                }
                return interactionChannel;
            };
            /**
             * Gets the interaction channel for a form by its root ID
             * @param rootId The root view model ID of the form
             * @returns The interaction channel for the form
             */
            InteractionManager.prototype._getInteractionChannelByRootId = function (rootId) {
                if (rootId && this._formToChannelMap[rootId]) {
                    return this._formToChannelMap[rootId];
                }
                if ($dyn.serverForm.defaultChannel) {
                    return $dyn.serverForm.defaultChannel();
                }
            };
            /**
             * Gets the interaction channel associated with an element (e.g. a control)
             * @param targetElement The element on the form
             * @returns The interaction channel for the form
             */
            InteractionManager.prototype._getInteractionChannelByElement = function (targetElement) {
                var rootId = $dyn.serverForm.interactionHandler.getRootIdByElement(targetElement);
                return this._getInteractionChannelByRootId(rootId);
            };
            /**
             * Gets an interaction channel from the channel pool which is not currently processing any interactions
             * @remarks Logs an error if no interaction channel is available
             * @returns An interaction channel that is not currently processing interactions
             */
            InteractionManager.prototype._getAvailableInteractionChannel = function () {
                for (var i = 0; i < this._channelPoolSize; i++) {
                    var channel = this._getInteractionChannel(false);
                    if (!channel.processingInteractions) {
                        return channel;
                    }
                }
                $dyn.logError("Unable to find a non-default interaction channel that is currently idle");
                return null;
            };
            /**
             * Gets the interaction channel used to open a form
             * @param defaultOnly Only return the default interaction channel
             * @returns
             * When `defaultOnly === true`, the default interaction channel.
             * Otherwise, the next available channel from the channel pool. It can be the default channel or one of the secondary channels.
             */
            InteractionManager.prototype._getInteractionChannel = function (defaultOnly) {
                if (!defaultOnly) {
                    var channel = this._channelPool[this._nextAvailableChannel];
                    this._nextAvailableChannel++;
                    if (this._nextAvailableChannel === this._channelPoolSize) {
                        this._nextAvailableChannel = 0;
                    }
                    return channel;
                }
                if ($dyn.serverForm.defaultChannel) {
                    return $dyn.serverForm.defaultChannel();
                }
            };
            InteractionManager.prototype.sendTelemetryEvents = function (interactions) {
                if (interactions) {
                    var message = {
                        Interactions: interactions,
                    };
                    var activityId_1 = $dyn.browserActivity.usePreviousActivityKey ? $dyn.browserActivity.getPreviousActivityId() : $dyn.util.newGuid();
                    $.ajax({
                        data: JSON.stringify(message),
                        url: "Services/TelemetryManager.svc/ProcessEventData",
                        // tslint:disable-next-line:object-literal-sort-keys
                        type: "POST",
                        contentType: "application/json; charset=utf-8",
                        dataType: "json",
                        headers: {
                            "ms-dyn-aid": activityId_1,
                            "ms-dyn-bsid": $dyn.browserActivity.BrowserSessionActivity.Id,
                            "ms-dyn-csrftoken": $dyn.serverForm.getCsrfTokenValue(),
                            "ms-dyn-sid": $dyn.serverForm.defaultChannel ? $dyn.serverForm.defaultChannel().channel.serverSessionId : "",
                        },
                        beforeSend: function (jqXHR, settings) {
                            if ($dyn.browserActivity.usePreviousActivityKey) {
                                $dyn.browserActivity.usePreviousActivityKey = false;
                            }
                            else {
                                $dyn.browserActivity && $dyn.browserActivity.newInteraction(activityId_1);
                            }
                            $dyn.ui.PerformanceTimer && $dyn.ui.PerformanceTimer._addAction("ProcessEventData");
                            $dyn.ui.PerformanceTimer && $dyn.ui.PerformanceTimer._startAjax(activityId_1);
                        },
                        complete: function () {
                            $dyn.ui.PerformanceTimer && $dyn.ui.PerformanceTimer._stopAjax();
                        },
                        timeout: 60 * 1000 /* 60 sec */,
                    });
                }
            };
            /**
             * Updates FormToChannelMap based on interactions received from the server
             * @param interaction Interaction received from the server
             * @param channel The interaction channel used to process the interaction
             */
            InteractionManager.prototype.updateMap = function (interaction, channel) {
                if (interaction.RootId) {
                    if (interaction.$type === $dyn.serverForm.InteractionType.updateViewModel && interaction.Descriptor.TypeName === "ClientUserSession") {
                        return;
                    }
                    else if (interaction.$type === $dyn.serverForm.InteractionType.changeViewModelId) {
                        if (this._formToChannelMap[interaction.PreviousValue]) {
                            delete this._formToChannelMap[interaction.PreviousValue];
                            this._formToChannelMap[interaction.NewValue] = channel;
                        }
                    }
                    else {
                        this._formToChannelMap[interaction.RootId] = channel;
                    }
                }
            };
            InteractionManager.prototype.reconstructCallbackFunctions = function (data) {
                // On the data, the interaction will be either a commandInteraction or interaction
                var interaction = data.interaction;
                if (interaction) {
                    var functionWindow = $dyn.serverForm.interactionManager._windowIdToHandler[interaction.windowId];
                    // Handle callbacks on the interaction
                    for (var property in interaction) {
                        if (interaction[property] && interaction[property]._functionId !== undefined && interaction[property]._functionId !== null) {
                            $dyn.log.navigation &&
                                $dyn.log.navigation("InteractionManager: reconstructing callback function: " + property + ", with functionId: " + interaction[property]._functionId + ", on Window: " + interaction.windowId);
                            this._createCallbackFunction(functionWindow, interaction, property);
                        }
                    }
                    // DO NOT convert data.callback to a function again. This will be handled later by the InteractionManager when processing interactions
                    // However since throttle callbacks are handled completely client-side they need a callback function generated
                    if (data.throttleCallback) {
                        this._createCallbackFunction(functionWindow, data, "throttleCallback");
                    }
                }
            };
            InteractionManager.prototype._createCallbackFunction = function (functionWindow, parentObject, callbackName) {
                var functionId = parentObject[callbackName]._functionId;
                var postMessageData = {
                    _functionId: functionId,
                    callbackName: callbackName,
                    source: "callback",
                };
                parentObject[callbackName] = function (result) {
                    postMessageData._result = result;
                    if (functionWindow) {
                        return $dyn.postMessage(functionWindow.window, { data: postMessageData });
                    }
                };
            };
            /**
             * Runs when an interaction is received
             */
            InteractionManager.prototype._onInteractionReceived = function (args) {
                if (args) {
                    this._processInteractions(args.Channel, args.Interactions, args.WindowId);
                }
            };
            /**
             * Buckets the given interactions to send to each window's InteractionHandler
             * @param channel
             * @param interactions The interactions to be handled
             * @param defaultWindowId
             */
            InteractionManager.prototype._processInteractions = function (channel, interactions, defaultWindowId) {
                var profiling;
                if ($dyn.debug.profileInteractions && !this._inboundBlocked && !$dyn.serverForm._profileInteractionsStarted) {
                    // Allow capturing of the profile of the interaction time (excluding static loading) if enabled.
                    console.profile("profileInteractions");
                    $dyn.serverForm._profileInteractionsStarted = true;
                    profiling = true;
                }
                $dyn.serverForm.processingInteractions(true);
                channel.processingInteractions = true;
                this._processInteractionsTask && this._processInteractionsTask.start(interactions.length.toString());
                // Command callbacks will be handled by the main window. Each command callback may internally post a message to a child window.
                var commandCallbackInteractions = [];
                // Separate the interactions for each root id.
                var interactionsByRootId = {};
                var windowIdToInteractions = {};
                var messagesByRootId = {};
                var windowIdToMessages = {};
                // tslint:disable-next-line:forin
                for (var id in this._windowIdToHandler) {
                    windowIdToInteractions[id] = [];
                    windowIdToMessages[id] = [];
                }
                var bucketInteractionByRootId = function (rootId, interaction) {
                    var interactionBucket = interactionsByRootId[rootId];
                    // If the bucket for this rootId does not exist, then create it.
                    if (!interactionBucket) {
                        interactionBucket = [];
                        interactionsByRootId[rootId] = interactionBucket;
                    }
                    interactionBucket.push(interaction);
                };
                var bucketMessagesByRootId = function (rootId, message) {
                    var messageBucket = messagesByRootId[rootId];
                    // If the bucket for this rootId does not exist, then create it.
                    if (!messageBucket) {
                        messageBucket = [];
                        messagesByRootId[rootId] = messageBucket;
                    }
                    messageBucket.push(message);
                };
                var concatMessageLogInteractions = function (windowInteractions, windowId) {
                    var messages = windowIdToMessages[windowId];
                    if (messages.length > 0) {
                        var newClientMessageLogInteraction = $dyn.serverForm.createClientMessageLogInteraction(messages);
                        windowInteractions = windowInteractions.concat([newClientMessageLogInteraction]);
                    }
                    return windowInteractions;
                };
                // Use the message's defaultWindowId
                if (!defaultWindowId) {
                    defaultWindowId = $dyn.serverForm._windowId;
                }
                for (var i = 0, len = interactions.length; i < len; i++) {
                    // 1) Sort the interactions to which root id they belong to.
                    // 2) update window->rootId and rootId->window mappings if appropriate
                    // 2) Call processInteractions on each handler.
                    var interaction = interactions[i];
                    var collectionInteraction = interaction;
                    var viewInteraction = interaction;
                    var windowId = this._rootIdToWindowId[interaction.RootId];
                    this.updateMap(interaction, channel);
                    switch (interaction.$type) {
                        case $dyn.serverForm.InteractionType.changeViewModelId:
                            // ChangeViewModelId interactions come in with RootId of Null. In this case, use its NewValue as the rootId
                            bucketInteractionByRootId(interaction.NewValue, interaction);
                            break;
                        case $dyn.serverForm.InteractionType.commandCallback:
                            commandCallbackInteractions.push(interaction);
                            break;
                        default:
                            // For all scenarios do the following:
                            // 1.) Sort by rootId
                            // 2.) User session and message center are on main window only
                            // 3.) If anchorRootId exists change the rootId->windowId mapping to point to the anchorRootId window
                            // 4.) Else send to default window
                            if (collectionInteraction.Descriptor && collectionInteraction.Descriptor.Name === "ClientMessageLog" && collectionInteraction.Descriptor.Items) {
                                // Bucket ClientMessageLog interactions separately for each rootId.
                                for (var j = 0, length_1 = collectionInteraction.Descriptor.Items.length; j < length_1; j++) {
                                    var message = collectionInteraction.Descriptor.Items[j];
                                    if (message.Item && message.Item.Properties && message.Item.Properties.formUniqueId) {
                                        bucketMessagesByRootId(message.Item.Properties.formUniqueId, message);
                                    }
                                }
                                continue;
                            }
                            // process any notifications
                            if (viewInteraction.Descriptor && viewInteraction.Descriptor.Name === "ClientNotifications") {
                                var json = viewInteraction.Descriptor.ValueProperties.Notifications;
                                if (json !== "") {
                                    var notifications = JSON.parse(json);
                                    $dyn.AddNotifications(notifications);
                                }
                                continue;
                            }
                            bucketInteractionByRootId(interaction.RootId, interaction);
                            if (viewInteraction.Descriptor && viewInteraction.Descriptor.Name === "ClientUserSession") {
                                this.addRootId(interaction.RootId, $dyn.serverForm._windowId);
                                continue;
                            }
                            // Try to get the windowId by anchorRootId
                            if (viewInteraction.Descriptor && viewInteraction.Descriptor.ValueProperties) {
                                var anchorRootId = viewInteraction.Descriptor.ValueProperties.anchorRootId;
                                if (typeof anchorRootId !== "undefined") {
                                    windowId = this._rootIdToWindowId[anchorRootId];
                                }
                            }
                            // If we still don't have a windowId, set it to the default for this message set.
                            if (typeof windowId === "undefined") {
                                windowId = defaultWindowId;
                            }
                            this.addRootId(interaction.RootId, windowId);
                            break;
                    }
                    // Tag Interactions for inline loading with channel id
                    if (viewInteraction.Descriptor && viewInteraction.Descriptor.ValueProperties && viewInteraction.Descriptor.ValueProperties.InlineLoadingKey) {
                        viewInteraction.Descriptor.ValueProperties.InlineLoadingKey = viewInteraction.Descriptor.ValueProperties.InlineLoadingKey + "_" + channel.channel.channelId;
                    }
                }
                this._bucketCommandCallbacks(commandCallbackInteractions, windowIdToInteractions);
                var windowIdToHandler = this._windowIdToHandler;
                // tslint:disable-next-line:forin
                for (var rootId in interactionsByRootId) {
                    var windowIdForThisRootId = this.getWindowIdFromRootId(rootId);
                    var rootInteractions = interactionsByRootId[rootId];
                    var windowInteractions = windowIdToInteractions[windowIdForThisRootId];
                    if (windowInteractions) {
                        for (var j = 0, len = rootInteractions.length; j < len; j++) {
                            windowInteractions.push(rootInteractions[j]);
                        }
                    }
                }
                // tslint:disable-next-line:forin
                for (var rootId in messagesByRootId) {
                    var windowIdForThisRootId = this.getWindowIdFromRootId(rootId, defaultWindowId);
                    var messages = messagesByRootId[rootId];
                    var windowMessages = windowIdToMessages[windowIdForThisRootId];
                    if (windowMessages) {
                        for (var j = 0, len = messages.length; j < len; j++) {
                            windowMessages.push(messages[j]);
                        }
                        // Potential number != string comparison
                        // tslint:disable-next-line:triple-equals
                        if (windowIdForThisRootId != 0) {
                            // Cache all messages going to popout windows for replay in the buffer
                            $dyn.serverForm.messageInteractionBuffer.addMessages(messages);
                        }
                    }
                }
                // Advance buffer after having a chance to cache all messages in this interaction loop
                $dyn.serverForm.messageInteractionBuffer.advanceBuffer();
                // tslint:disable-next-line:forin
                for (var currentWindowId in windowIdToInteractions) {
                    var windowInteractions = windowIdToInteractions[currentWindowId];
                    windowInteractions = windowInteractions && concatMessageLogInteractions(windowInteractions, currentWindowId);
                    if (windowInteractions && windowInteractions.length > 0) {
                        // If we have interactions to send to the handler, tell the handler to process them.
                        var interactionHandler_1 = windowIdToHandler[currentWindowId];
                        interactionHandler_1 && interactionHandler_1.processInteractions(windowInteractions);
                    }
                }
                this._processInteractionsTask && this._processInteractionsTask.stop();
                channel.processingInteractions = false;
                $dyn.serverForm.processingInteractions(false);
                if ($dyn.debug.profileInteractions && profiling) {
                    console.profileEnd();
                    $dyn.serverForm._profileInteractionsStarted = false;
                    profiling = false;
                }
            };
            /**
             * Bucket command callback interactions to be processed by the appropriate window
             * @param interactions Interactions to process
             * @param windowIdToInteractions Map of Window ID to a list of interactions
             */
            InteractionManager.prototype._bucketCommandCallbacks = function (interactions, windowIdToInteractions) {
                for (var i = 0, len = interactions.length; i < len; i++) {
                    var interaction = interactions[i];
                    $dyn.log.server && $dyn.log.server("ServerForm: Handling callback interaction: %o", interaction);
                    if (interaction.CallbackId in this.commandCallbacks) {
                        var callback = this.commandCallbacks[interaction.CallbackId];
                        interaction.callback = callback;
                        var windowId = interaction.callback.windowId;
                        if (!windowId) {
                            windowId = $dyn.serverForm._windowId;
                        }
                        windowIdToInteractions[windowId] && windowIdToInteractions[windowId].push(interaction);
                        delete this.commandCallbacks[interaction.CallbackId];
                        delete interaction.CallbackId;
                    }
                    // If a command has a CommandCallback and a FailureCallback registered,
                    // only one of them will be executed (as the CallbackId) and the other will not be used (the UnusedCallbackId).
                    if (interaction.UnusedCallbackId in this.commandCallbacks) {
                        delete this.commandCallbacks[interaction.UnusedCallbackId];
                        delete interaction.UnusedCallbackId;
                    }
                }
            };
            return InteractionManager;
        }());
        serverForm.InteractionManager = InteractionManager;
        // Statically initialize an interaction manager for QUnit tests. $dyn.serverForm.init() will re-initialize appropriately.
        serverForm.interactionManager = new InteractionManager();
    })(serverForm = $dyn.serverForm || ($dyn.serverForm = {}));
})($dyn || ($dyn = {}));

//# sourceMappingURL=InteractionManager.js.map
